<template>
  <div class="px-4">
    <div
      @dragover.prevent
      @dragenter.prevent
      @dragleave.prevent
      @drop.prevent="handleDrop"
      @click="$refs.fileInput.click()"
      class="border-2 border-dashed rounded-md border-white border-opacity-20 my-4 p-10 
      text-center hover:cursor-pointer hover:border-sp-primary duration-150"
    >
      {{ $t("attach") }}
    </div>

    <input
      type="file"
      ref="fileInput"
      @change="uploadFile"
      multiple
      :accept="acceptedFormats"
      :disabled="spinnerShown"
      id="fileUpload"
      name="fileUpload"
      style="display: none"
    />

    <div class="flex gap-4 flex-wrap">
      <div v-for="(file, index) in uploadedFiles" :key="index" class="flex items-center break-all">
        <span>{{ file.name }}</span>
        <button @click="deleteFile(index, $event)" class="ml-2 hover:text-sp-accent">
          <i @click="isOpen = false" 
          class="ri-close-line text-2xl text-sp-primary border-white border-opacity-20 hover:border-sp-secondary 
          cursor-pointer border-2 rounded-md"></i>
        </button>
      </div>
    </div>
  </div>

  <Modal v-if="activeModal" :message="getTranslation(activeModal)" @closed="closeModal" />
  <Spinner v-if="spinnerShown" />
</template>

<script>
import Modal from './Modal.vue'
import Spinner from './Spinner.vue'
import * as collectData from '../modules/collectData.js'
import { reportError } from '../utils/reporting'

export default {
  components: {
    Modal,
    Spinner
  },
  props: {
    maxFiles: {
      type: Number,
      default: 4
    },
    maxFileSize: {
      type: Number,
      default: 10485760
    },
  },
  emits: ['filesUpdated'],
  data() {
    return {
      acceptedFormats: '.jpg,.jpeg,.png,.bmp,.tif,.tiff,.gif,.webp,.avi,.mp4,.mov,.flv,.mkv,.webm,.txt,.csv',
      uploadedFiles: [],
      fileTokens: [],
      index: 0,
      activeModal: null,
      spinnerShown: false
    }
  },
  methods: {
    getTranslation(messageKey) {
      return this.$t(messageKey)
    },
    async uploadFile() {
      const fileInput = this.$refs.fileInput
      const files = fileInput.files
      await this.processFiles(files)
    },
    async handleDrop(event) {
      event.preventDefault()
      const files = event.dataTransfer.files
      await this.processFiles(files)
    },
    async processFiles(files) {
      for (let i = 0; i < files.length; i++) {
        if (this.uploadedFiles.length >= this.maxFiles) {
          this.activeModal = 'numberExceeded'
          return
        }

        if (files[i].size > this.maxFileSize) {
          this.activeModal = 'sizeExceeded'
          return
        }

        const acceptedExtensions = this.acceptedFormats.split(',')
        const fileExtension = '.' + files[i].name.split('.').pop().toLowerCase()
        if (!acceptedExtensions.includes(fileExtension)) {
          this.activeModal = 'wrongFormat'
          return
        }

        const userData = collectData.getUserData()
        const apiUrl = import.meta.env.VITE_API_URL
        const url = apiUrl + 'files' + 
        `?game=${userData.game}&platform=${userData.platform}&uid=${userData.uid}&token=${userData.signature}`
        const formData = new FormData()
        formData.append('file', files[i])

        const headers = new Headers()
        headers.append("Accept", "application/json")
        headers.append("Access-Control-Allow-Origin", "*")

        const requestOptions = {
          method: 'POST',
          headers,
          body: formData,
          redirect: 'follow'
        }

        try {
          this.spinnerShown = true
          const response = await fetch(url, requestOptions)

          if (response.ok) {
            const data = await response.json()
            if (!data.isSuccess) {
              reportError('file upload: isSuccess=false', { source: 'FileUploader.processFiles' })
              this.$router.push({ name: 'helpcenter' })
              return
            }
            const uploadedFile = {
              name: data.answer.fileName,
              token: data.answer.token,
              file: files[i]
            }
            this.uploadedFiles.push(uploadedFile)
            this.fileTokens.push(uploadedFile.token)
            this.$emit('files-updated', this.fileTokens)
          } else {
            reportError(`file upload HTTP ${response.status}`, { source: 'FileUploader.processFiles', status: response.status })
            this.$router.push({ name: 'helpcenter' })
          }
        } catch (error) {
          reportError(error, { source: 'FileUploader.processFiles' })
        } finally {
          this.spinnerShown = false
        }
      }
    },
    async deleteFile(index, event) {
      event.preventDefault()
      const fileToken = this.uploadedFiles[index].token
      const userData = collectData.getUserData()
      const apiUrl = import.meta.env.VITE_API_URL
      const url = apiUrl + 'files' + `?file_token=${fileToken}` +
      `&game=${userData.game}&platform=${userData.platform}&uid=${userData.uid}&token=${userData.signature}`

      try {
        this.spinnerShown = true

        const response = await fetch(url, {
          method: 'DELETE'
        })

        if (response.ok) {
          const data = await response.json()
          if (!data.isSuccess) {
            reportError('file delete: isSuccess=false', { source: 'FileUploader.deleteFile' })
            this.$router.push({ name: 'helpcenter' })
            return
          }
          this.uploadedFiles.splice(index, 1)
          this.fileTokens.splice(index, 1)
          this.$emit('files-updated', this.fileTokens)
        } else {
          reportError(`file delete HTTP ${response.status}`, { source: 'FileUploader.deleteFile', status: response.status })
          this.$router.push({ name: 'helpcenter' })
        }
      } catch (error) {
        reportError(error, { source: 'FileUploader.deleteFile' })
        this.$router.push({ name: 'helpcenter' })
      } finally {
        this.spinnerShown = false
      }
    },
    closeModal() {
      this.activeModal = null
    }
  }
}
</script>
